import {
  FC,
  useCallback,
  useEffect,
  useState,
  useInsertionEffect,
} from 'react';

import io from 'socket.io-client';
import { useAppConfig } from '~/providers/app';
import { env } from '~/env';
import { useToast } from '@chakra-ui/react';

import { api } from '~/services/api';

import { CallPriorityEnum } from '~/enums/CallPriorityEnum';

import { CallList, Container, NoCallsContainer } from './styles';

import { Card } from '../Card';

interface ICall {
  readonly _id: string;
  readonly configurationId: string;
  readonly companyDocument: string;
  readonly tableNumber: string;
  readonly calledAt: Date;
  readonly priority: CallPriorityEnum;
}

type TSocketMessageTypes = 'close-call' | 'new-call';

interface ISocketMessage {
  readonly type: TSocketMessageTypes;
  readonly data?: ICall;
  readonly id?: string;
}

const socket = io(`${env.API_URL}/calls`, { transports: ['websocket'] });
export const Body: FC = () => {
  const { config, putError } = useAppConfig();
  const [calls, setCalls] = useState<ICall[]>([]);

  const toast = useToast({
    position: 'top-right',
    duration: 25000,
  });

  const mergeCalls = useCallback((callsToMerge: ICall[]) => {
    setCalls((prevState) => {
      const newCalls = [...(prevState || [])];
      callsToMerge.forEach((call) => {
        const found = newCalls.find((_call) => _call._id === call._id);
        if (!found) {
          newCalls.push(call);
        }
      });

      return newCalls;
    });
  }, []);

  const addCall = useCallback((newCall: ICall) => {
    setCalls((prevState) => [...prevState, newCall]);
  }, []);

  const clearCall = useCallback((callId: string) => {
    setCalls((prevState) => prevState.filter((card) => card._id !== callId));
  }, []);

  const expireCall = useCallback(
    async (callId: string, tableNumber: string) => {
      try {
        await api.post(`/calls/close/${callId}`);
      } catch {
        toast({
          status: 'error',
          description: `Houve um erro ao fechar automaticamente o chamado ${tableNumber}`,
        });
      }
    },
    [toast]
  );

  const loadCalls = useCallback(async () => {
    if (!config) return;
    try {
      const { data: response } = await api.get<ICall[]>(`/calls/${config._id}`);
      mergeCalls(response);
      putError(null);
    } catch {
      putError('Houve um erro ao consultar os chamados em aberto.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, mergeCalls]);

  useInsertionEffect(() => {
    loadCalls();
    const thirtyMinutesInMilliseconds = 1000 * 60 * 30;
    const ordersInterval = setInterval(loadCalls, thirtyMinutesInMilliseconds);
    return () => clearInterval(ordersInterval);
  }, []);

  useEffect(() => {
    if (!config) return;
    const { companyDocument } = config;
    socket.on(`call-${companyDocument}`, (message: ISocketMessage) => {
      const { type, data, id } = message;
      if (type === 'new-call') {
        addCall(data);
      } else if (type === 'close-call') {
        clearCall(id);
      }
    });

    return () => {
      socket.off(`call-${companyDocument}`);
    };
  }, [addCall, clearCall, config]);

  return (
    <Container>
      {calls.length > 0 ? (
        <CallList>
          {calls.map((call) => (
            <Card
              key={call._id}
              tableNumber={call.tableNumber}
              calledAt={call.calledAt}
              ranges={config?.priorityRanges}
              limitMinutes={config.limitCallMinutes}
              onExpire={() => expireCall(call._id, call.tableNumber)}
            />
          ))}
        </CallList>
      ) : (
        <NoCallsContainer>
          <span>Nenhum chamado em aberto</span>
        </NoCallsContainer>
      )}
    </Container>
  );
};
